import { useContext, useEffect } from 'react';

import { AppContext } from '../context/context';

import useEvent from './useEvent';

const KEY_DELIMITER = ',';

export type UseSocketEventOptions = {
  /** When true, events are queued and handled one at a time (awaits async handlers). */
  processOneAtATime?: boolean;
};

const useSocketEvent = <const T extends unknown[]>(
  messages: string | string[],
  handler: (...args: T) => void | Promise<void>,
  options?: UseSocketEventOptions,
) => {
  const { socket } = useContext(AppContext);
  const processOneAtATime = options?.processOneAtATime ?? false;

  const handlerInternal = useEvent(handler);

  const messageArray = Array.isArray(messages) ? messages : [messages];
  const messagesKey = messageArray.join(KEY_DELIMITER);

  useEffect(() => {
    if (!socket) return undefined;

    const messageKeys = messagesKey.split(KEY_DELIMITER);

    if (!processOneAtATime) {
      messageKeys.forEach((msg) => {
        socket.on(msg, handlerInternal);
      });

      return () =>
        messageKeys.forEach((msg) => {
          socket.off(msg, handlerInternal);
        });
    }

    const queue: T[] = [];
    let isProcessing = false;

    const drainQueue = async (): Promise<void> => {
      const args = queue.shift();

      if (!args) {
        return;
      }

      try {
        await handlerInternal(...args);
      } catch {
        // Keep draining the queue even when a handler throws.
      }

      await drainQueue();
    };

    const processQueue = (): undefined => {
      if (isProcessing) {
        return undefined;
      }

      isProcessing = true;

      drainQueue().finally(() => {
        isProcessing = false;

        if (queue.length > 0) {
          processQueue();
        }
      });

      return undefined;
    };

    const onMessage = (...args: T) => {
      queue.push(args);
      processQueue();

      return undefined;
    };

    messageKeys.forEach((msg) => {
      socket.on(msg, onMessage);
    });

    return () => {
      messageKeys.forEach((msg) => {
        socket.off(msg, onMessage);
      });
      queue.length = 0;
    };
  }, [handlerInternal, messagesKey, processOneAtATime, socket]);
};

export default useSocketEvent;
